import { useState, useEffect } from 'react';

/**
 * "You already have a card under a Google sign-in."
 *
 * Shown once, after an Apple signup whose email matches an existing profile.
 *
 * This is a COURTESY, not a control, and it cannot be turned into one. A
 * zkLogin address is H(iss, aud, sub, salt) — Apple and Google differ on all
 * three of the first values, so the same human signing in with each gets two
 * different wallets. Merging them is not a feature we declined to build; it is
 * cryptographically impossible. All this can do is stop someone with 9 stamps
 * concluding the app lost them.
 *
 * Apple's Hide My Email defeats the match entirely, so this will often not
 * appear when it "should". That is expected — see api/_lib/existing-account.ts.
 */
function ExistingCardNotice() {
  const [provider, setProvider] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const existing = params.get('existing');
    if (!existing) return;
    // Only ever render a provider we actually support — the value arrives via
    // the URL, so treat it as untrusted text rather than something to print.
    setProvider(existing === 'google' ? 'GOOGLE' : existing === 'apple' ? 'APPLE' : null);

    // Consume it immediately so a refresh or a shared link does not replay the
    // notice at someone who never signed up with Apple.
    params.delete('existing');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []);

  if (!provider) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md">
      <div className="bg-white rounded-[16px] p-5 shadow-pixel">
        <p className="font-heading text-[9px] text-coral tracking-[1px] mb-2">HEADS UP</p>
        <p className="font-grotesk font-medium text-[13px] text-body-grey leading-relaxed mb-3">
          You already have a card under a {provider} sign-in with this email. This
          Apple card starts fresh — the two can&apos;t be combined. Sign out and use{' '}
          {provider} to get back to your original stamps.
        </p>
        <button
          onClick={() => setProvider(null)}
          className="w-full rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3"
        >
          GOT IT
        </button>
      </div>
    </div>
  );
}

export default ExistingCardNotice;
